import * as React from 'react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { toKebabCase } from '@klareospace/pharos-lib-utils';
import {
  Button,
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  SideDrawer,
} from '@klareospace/pharos-lib-web-utils';

import type { CreateOrderType, OrderType, UpdateOrderType } from '@klareospace/pharos-lib-types';

import { useOrderTypeForm } from '../hooks/use-order-type-form';

// ─── Types ────────────────────────────────────────────────────────────────────

interface OrderTypeDrawerProps {
  isPending: boolean;
  onClose: () => void;
  onSubmit: (data: CreateOrderType | UpdateOrderType) => Promise<void>;
  open: boolean;
  orderType: OrderType | null;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function OrderTypeDrawer({
  isPending,
  onClose,
  onSubmit,
  open,
  orderType,
}: OrderTypeDrawerProps): React.JSX.Element {
  const { t } = useTranslation('settings');
  const isEditMode = orderType !== null;

  const { codeManuallyEdited, form, handleFormSubmit } = useOrderTypeForm({
    isEditMode,
    isPending,
    onClose,
    onSubmit,
    orderType,
  });

  useEffect(() => {
    if (open) {
      codeManuallyEdited.current = false;
      form.reset({
        code: isEditMode && orderType ? orderType.code : '',
        label: isEditMode && orderType ? orderType.label : '',
      });
    }
  }, [open, isEditMode, orderType, form, codeManuallyEdited]);

  const title = isEditMode ? t('orderTypes.drawer.editTitle') : t('orderTypes.drawer.title');
  const subtitle = isEditMode ? t('orderTypes.drawer.editSubtitle') : undefined;

  function handleLabelChange(
    event: React.ChangeEvent<HTMLInputElement>,
    fieldOnChange: (event: React.ChangeEvent<HTMLInputElement>) => void,
  ): void {
    fieldOnChange(event);
    if (!isEditMode && !codeManuallyEdited.current) {
      form.setValue('code', toKebabCase(event.target.value), { shouldValidate: true });
    }
  }

  function handleCodeChange(
    event: React.ChangeEvent<HTMLInputElement>,
    fieldOnChange: (event: React.ChangeEvent<HTMLInputElement>) => void,
  ): void {
    codeManuallyEdited.current = true;
    fieldOnChange(event);
  }

  function handleNativeSubmit(e: React.FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    void form.handleSubmit(handleFormSubmit)(e);
  }

  function handleOpenChange(nextOpen: boolean): void {
    if (!nextOpen) {
      form.reset();
      onClose();
    }
  }

  const footer = (
    <>
      <Button
        disabled={isPending || !form.formState.isValid || form.formState.isSubmitting}
        form="order-type-drawer-form"
        type="submit"
      >
        {isEditMode ? t('orderTypes.drawer.save') : t('orderTypes.drawer.create')}
      </Button>
      <Button onClick={(): void => handleOpenChange(false)} type="button" variant="outline">
        {t('orderTypes.drawer.cancel')}
      </Button>
    </>
  );

  return (
    <SideDrawer footer={footer} onOpenChange={handleOpenChange} open={open} subtitle={subtitle} title={title}>
      <Form {...form}>
        <form className="space-y-4" id="order-type-drawer-form" onSubmit={handleNativeSubmit}>
          <FormField
            control={form.control}
            name="label"
            render={({ field }): React.JSX.Element => (
              <FormItem>
                <FormLabel>
                  {t('orderTypes.drawer.fields.label')} <span aria-hidden="true">*</span>
                </FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    maxLength={100}
                    onChange={(e): void => handleLabelChange(e, field.onChange)}
                    placeholder={t('orderTypes.drawer.fields.labelPlaceholder')}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="code"
            render={({ field }): React.JSX.Element => (
              <FormItem>
                <FormLabel>
                  {t('orderTypes.drawer.fields.code')} <span aria-hidden="true">*</span>
                </FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    className="font-mono text-xs"
                    disabled={isEditMode}
                    maxLength={100}
                    onChange={(e): void => handleCodeChange(e, field.onChange)}
                    placeholder={t('orderTypes.drawer.fields.codePlaceholder')}
                  />
                </FormControl>
                {!isEditMode && <FormDescription>{t('orderTypes.drawer.fields.codeHelp')}</FormDescription>}
                <FormMessage />
              </FormItem>
            )}
          />
        </form>
      </Form>
    </SideDrawer>
  );
}
